import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Layout container                                                    */
/* ------------------------------------------------------------------ */
export const Container = ({ className = "", children, ...rest }) => (
  <div className={`mx-auto w-full max-w-[1360px] px-6 md:px-10 lg:px-16 ${className}`} {...rest}>
    {children}
  </div>
);

/* ------------------------------------------------------------------ */
/* Overline / eyebrow                                                  */
/* ------------------------------------------------------------------ */
export const Overline = ({ children, className = "", accent = false }) => (
  <span
    className={`overline inline-block ${accent ? "text-accent" : "text-muted-foreground"} ${className}`}
  >
    {children}
  </span>
);

/* ------------------------------------------------------------------ */
/* Scroll reveal                                                       */
/* ------------------------------------------------------------------ */
// When the build renders a page to HTML (no window), show the content as-is so
// the words in the file are visible rather than faded out.
export const Reveal = ({ children, delay = 0, className = "", y = 24 }) =>
  typeof window === "undefined" ? (
    <div className={className}>{children}</div>
  ) : (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
    className={className}
  >
    {children}
  </motion.div>
);

/* ------------------------------------------------------------------ */
/* Buttons (as links)                                                  */
/* ------------------------------------------------------------------ */
const isExternal = (href) => href?.startsWith("http") || href?.startsWith("#");

export const PrimaryButton = ({ to = "/book-a-walkthrough", children, testid, className = "", arrow = true }) => {
  const cls =
    `group inline-flex items-center gap-3 bg-primary px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-primary-foreground transition-colors duration-200 hover:bg-accent hover:text-accent-foreground ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      {arrow && (
        <ArrowRight
          strokeWidth={1.6}
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
        />
      )}
    </>
  );
  if (isExternal(to)) return <a href={to} data-testid={testid} className={cls}>{inner}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
};

export const SecondaryButton = ({ to = "/", children, testid, className = "", arrow = false }) => {
  const cls =
    `group inline-flex items-center gap-3 border border-foreground/40 px-7 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-foreground transition-colors duration-200 hover:border-accent hover:text-accent ${className}`;
  const inner = (
    <>
      <span>{children}</span>
      {arrow && (
        <ArrowRight
          strokeWidth={1.6}
          className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
        />
      )}
    </>
  );
  if (isExternal(to)) return <a href={to} data-testid={testid} className={cls}>{inner}</a>;
  return <Link to={to} data-testid={testid} className={cls}>{inner}</Link>;
};

export const TextLink = ({ to, children, testid, className = "" }) => (
  <Link
    to={to}
    data-testid={testid}
    className={`group inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-foreground transition-colors hover:text-accent ${className}`}
  >
    <span className="border-b border-transparent pb-1 transition-colors group-hover:border-accent">
      {children}
    </span>
    <ArrowRight strokeWidth={1.6} className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
  </Link>
);

/* ------------------------------------------------------------------ */
/* Section heading                                                     */
/* ------------------------------------------------------------------ */
export const SectionHeading = ({ eyebrow, title, intro, className = "", accentEyebrow = true }) => (
  <div className={`max-w-3xl ${className}`}>
    {eyebrow && <Overline accent={accentEyebrow}>{eyebrow}</Overline>}
    <h2 className="mt-5 font-display text-4xl font-semibold leading-[1.05] tracking-tight text-foreground md:text-5xl lg:text-[3.4rem]">
      {title}
    </h2>
    {intro && (
      <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">{intro}</p>
    )}
  </div>
);

/* ------------------------------------------------------------------ */
/* Bulleted list with hairline rows                                    */
/* ------------------------------------------------------------------ */
export const HairlineList = ({ items, testid }) => (
  <ul data-testid={testid} className="mt-2 border-t border-border">
    {items.map((item, i) => (
      <li
        key={i}
        className="flex items-start gap-4 border-b border-border py-4 text-[0.98rem] leading-relaxed text-foreground/90"
      >
        <span className="mt-[0.55rem] h-[6px] w-[6px] shrink-0 bg-accent" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

/* ------------------------------------------------------------------ */
/* Numbered index label                                                */
/* ------------------------------------------------------------------ */
export const IndexLabel = ({ n }) => (
  <span className="font-body text-sm font-medium tracking-widest text-accent">{n}</span>
);
